import axios from 'axios';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addMotiveExceptionRequest,
  deleteMotiveExceptionRequest,
  getMotiveExceptionsRequest,
  updateMotiveExceptionRequest,
} from 'api/requests/motive-exception-requests';
import {
  MOTIVE_EXCEPTIONS__ADD,
  MOTIVE_EXCEPTIONS__ADD_ERROR,
  MOTIVE_EXCEPTIONS__ADD_SUCCESS,
  MOTIVE_EXCEPTIONS__CLEAR_ERRORS,
  MOTIVE_EXCEPTIONS__DELETE,
  MOTIVE_EXCEPTIONS__DELETE_ERROR,
  MOTIVE_EXCEPTIONS__DELETE_SUCCESS,
  MOTIVE_EXCEPTIONS__GET,
  MOTIVE_EXCEPTIONS__SET,
  MOTIVE_EXCEPTIONS__SET_ERROR,
  MOTIVE_EXCEPTIONS__SET_LOADING,
  MOTIVE_EXCEPTIONS__UPDATE,
  MOTIVE_EXCEPTIONS__UPDATE_ERROR,
  MOTIVE_EXCEPTIONS__UPDATE_SUCCESS,
} from 'store/constants';
import IMotiveException from 'types/company/payroll/IMotiveException';
import logError from 'utils/log-error';

export const setMotiveExceptionsLoadingAction = createAction<boolean>(MOTIVE_EXCEPTIONS__SET_LOADING);
export const setMotiveExceptionsAction = createAction<IMotiveException[]>(MOTIVE_EXCEPTIONS__SET);
export const setMotiveExceptionsErrorAction = createAction<boolean>(MOTIVE_EXCEPTIONS__SET_ERROR);

export const getMotiveExceptionsAction = createAsyncThunk(
  MOTIVE_EXCEPTIONS__GET,
  async (companyId: number, thunkApi) => {
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(true));
    thunkApi.dispatch(setMotiveExceptionsErrorAction(false));
    try {
      const response = await getMotiveExceptionsRequest(companyId);
      thunkApi.dispatch(setMotiveExceptionsAction(response));
    } catch (error) {
      logError(error);
      thunkApi.dispatch(setMotiveExceptionsErrorAction(true));
    }
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(false));
  },
);

export const addMotiveExceptionSuccessAction = createAction<boolean>(MOTIVE_EXCEPTIONS__ADD_SUCCESS);
export const addMotiveExceptionErrorAction = createAction<string>(MOTIVE_EXCEPTIONS__ADD_ERROR);

export const addMotiveExceptionAction = createAsyncThunk(
  MOTIVE_EXCEPTIONS__ADD,
  async (props: { companyId: number; motiveException: IMotiveException }, thunkApi) => {
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(true));
    thunkApi.dispatch(addMotiveExceptionErrorAction(''));
    thunkApi.dispatch(addMotiveExceptionSuccessAction(false));
    try {
      await addMotiveExceptionRequest(props);
      thunkApi.dispatch(addMotiveExceptionSuccessAction(true));
      thunkApi.dispatch(getMotiveExceptionsAction(props.companyId));
    } catch (error) {
      logError(error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        thunkApi.dispatch(addMotiveExceptionErrorAction(error.response.data));
      }
    }
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(false));
  },
);

export const updateMotiveExceptionSuccessAction = createAction<boolean>(MOTIVE_EXCEPTIONS__UPDATE_SUCCESS);
export const updateMotiveExceptionErrorAction = createAction<string>(MOTIVE_EXCEPTIONS__UPDATE_ERROR);

export const updateMotiveExceptionAction = createAsyncThunk(
  MOTIVE_EXCEPTIONS__UPDATE,
  async (props: { companyId: number; motiveException: IMotiveException }, thunkApi) => {
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(true));
    thunkApi.dispatch(updateMotiveExceptionErrorAction(''));
    thunkApi.dispatch(updateMotiveExceptionSuccessAction(false));
    try {
      await updateMotiveExceptionRequest(props);
      thunkApi.dispatch(updateMotiveExceptionSuccessAction(true));
      thunkApi.dispatch(getMotiveExceptionsAction(props.companyId));
    } catch (error) {
      logError(error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        thunkApi.dispatch(updateMotiveExceptionErrorAction(error.response.data));
      }
    }
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(false));
  },
);

export const deleteMotiveExceptionSuccessAction = createAction<boolean>(MOTIVE_EXCEPTIONS__DELETE_SUCCESS);
export const deleteMotiveExceptionErrorAction = createAction<string>(MOTIVE_EXCEPTIONS__DELETE_ERROR);

export const deleteMotiveExceptionAction = createAsyncThunk(
  MOTIVE_EXCEPTIONS__DELETE,
  async (props: { companyId: number; id: number }, thunkApi) => {
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(true));
    thunkApi.dispatch(deleteMotiveExceptionErrorAction(''));
    thunkApi.dispatch(deleteMotiveExceptionSuccessAction(false));
    try {
      await deleteMotiveExceptionRequest(props);
      thunkApi.dispatch(deleteMotiveExceptionSuccessAction(true));
      thunkApi.dispatch(getMotiveExceptionsAction(props.companyId));
    } catch (error) {
      logError(error);
      if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
        thunkApi.dispatch(deleteMotiveExceptionErrorAction(error.response.data));
      }
    }
    thunkApi.dispatch(setMotiveExceptionsLoadingAction(false));
  },
);

export const clearMotiveExceptionErrorsAction = createAction<void>(MOTIVE_EXCEPTIONS__CLEAR_ERRORS);
